import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  getMutualDashboardICQuery,
  getMutualDashboardFDMQuery,
  getMutualDashboardEDQuery,
  getMutualDashboardExitsQuery,
  getMutualDashboardCLQuery,
  getMutualDashboardOKQuery,
} from "./queries";
import { QUERY_STATUS } from "constants/constants";
import _ from "lodash";
import { canUserPerformAction } from "access-roles/general-functions";
import { MUTUAL_DASHBOARD_ROLES } from "access-roles/roles";
import { useUserContext } from "api/user/UserProvider";
import { streamingDownload } from "utils/streamingDownload";
import { buildQueryParams } from "utils/queriesHelper";

const MutualDashboardContext = createContext({});

export const useMutualDashboardContext = () =>
  useContext(MutualDashboardContext);

export const MutualDashboardProvider = ({ ...props }) => {
  const [mutualDashboardICData, setMutualDashboardICData] = useState({});
  const [mutualDashboardFDMData, setMutualDashboardFDMData] = useState({});
  const [mutualDashboardEDData, setMutualDashboardEDData] = useState({});
  const [mutualDashboardExitsData, setMutualDashboardExitsData] = useState({});
  const { userRoleList, userScope } = useUserContext();

  const [mutualDashboardCLData, setMutualDashboardCLData] = useState({});
  const [mutualDashboardOKData, setMutualDashboardOKData] = useState({});

  const [loadICDataStatus, setLoadICDataStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [loadFDMDataStatus, setLoadFDMDataStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [loadEDDataStatus, setLoadEDDataStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [loadOKDataStatus, setLoadOKDataStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [loadCLDataStatus, setLoadCLDataStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [loadExitsDataStatus, setLoadExitsDataStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [loadExportStatus, setLoadExportStatus] = useState(
    QUERY_STATUS.INITIAL
  );

  const getMutualDashboardCSVFile = useCallback(
    async ({ section, lang, params }) => {
      try {
        setLoadExportStatus(QUERY_STATUS.LOADING);
        await streamingDownload({
          path: `/dashboard/mutual/${section}/export?${buildQueryParams({
            ...params,
            lang,
          })}`,
          fileName: `mutual_${section}_export.xlsx`,
          mimeType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        setLoadExportStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setLoadExportStatus(QUERY_STATUS.ERROR);

        console.error(
          "Erreur lors de la génération ou du téléchargement du fichier:",
          error
        );
      }
    },
    [userScope]
  );

  const mutualDashboardICDataGetter = useCallback(
    async ({ subsidiary, date, manager, job, jobCategory, page = 1 }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: MUTUAL_DASHBOARD_ROLES.ROLE_GET_MUTUAL_IC,
        })
      ) {
        return;
      }
      try {
        setLoadICDataStatus(QUERY_STATUS.LOADING);
        const result = await getMutualDashboardICQuery({
          scope: userScope,
          subsidiary,
          date,
          manager,
          job,
          jobCategory,
          page,
        });
        setMutualDashboardICData((old) => {
          return _.isEmpty(old.data)
            ? {
                ...old,
                total: result?.total_items,
                data: result?.content,
                page: 1,
              }
            : {
                ...old,
                page,
                data: _.concat(old?.data, result?.content),
              };
        });
        setLoadICDataStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setLoadICDataStatus(QUERY_STATUS.ERROR);
        console.error("mutualDashboardICDataGetter", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const mutualDashboardCLDataGetter = useCallback(
    async ({ subsidiary, manager, date, page = 1 }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: MUTUAL_DASHBOARD_ROLES.ROLE_GET_MUTUAL_CL,
        })
      ) {
        return;
      }
      try {
        setLoadCLDataStatus(QUERY_STATUS.LOADING);
        const result = await getMutualDashboardCLQuery({
          scope: userScope,
          subsidiary,
          manager,
          date,
          page,
        });
        setMutualDashboardCLData((old) => {
          return _.isEmpty(old.data)
            ? {
                ...old,
                total: result?.total_items,
                data: result?.content,
                page: 1,
              }
            : {
                ...old,
                page,
                data: _.concat(old?.data, result?.content),
              };
        });
        setLoadCLDataStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setLoadCLDataStatus(QUERY_STATUS.ERROR);
        console.error("mutualDashboardCLDataGetter", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const mutualDashboardFDMDataGetter = useCallback(
    async ({ subsidiary, manager, date, job, jobCategory, page = 1 }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: MUTUAL_DASHBOARD_ROLES.ROLE_GET_MUTUAL_FDM,
        })
      ) {
        return;
      }
      try {
        setLoadFDMDataStatus(QUERY_STATUS.LOADING);
        const result = await getMutualDashboardFDMQuery({
          scope: userScope,
          subsidiary,
          manager,
          date,
          job,
          jobCategory,
          page,
        });
        setMutualDashboardFDMData((old) => {
          return _.isEmpty(old.data)
            ? {
                ...old,
                total: result?.total_items,
                data: result?.content,
                page: 1,
              }
            : {
                ...old,
                page,
                data: _.concat(old?.data, result?.content),
              };
        });
        setLoadFDMDataStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setLoadFDMDataStatus(QUERY_STATUS.ERROR);
        console.error("mutualDashboardFDMDataGetter", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const mutualDashboardEDDataGetter = useCallback(
    async ({ subsidiary, manager, date, job, jobCategory, page = 1 }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: MUTUAL_DASHBOARD_ROLES.ROLE_GET_MUTUAL_ED,
        })
      ) {
        return;
      }
      try {
        setLoadEDDataStatus(QUERY_STATUS.LOADING);
        const result = await getMutualDashboardEDQuery({
          scope: userScope,
          subsidiary,
          manager,
          date,
          job,
          jobCategory,
          page,
        });
        setMutualDashboardEDData((old) => {
          return _.isEmpty(old.data)
            ? {
                ...old,
                total: result?.total_items,
                data: result?.content,
                page: 1,
              }
            : {
                ...old,
                page,
                data: _.concat(old?.data, result?.content),
              };
        });
        setLoadEDDataStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setLoadEDDataStatus(QUERY_STATUS.ERROR);
        console.error("mutualDashboardEDDataGetter", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const mutualDashboardExitsDataGetter = useCallback(
    async ({ subsidiary, manager, worker_type, date, page = 1 }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: MUTUAL_DASHBOARD_ROLES.ROLE_GET_MUTUAL_KO,
        })
      ) {
        return;
      }
      try {
        setLoadExitsDataStatus(QUERY_STATUS.LOADING);
        const result = await getMutualDashboardExitsQuery({
          scope: userScope,
          subsidiary,
          manager,
          worker_type,
          date,
          page,
        });
        setMutualDashboardExitsData((old) => {
          return _.isEmpty(old.data)
            ? {
                ...old,
                total: result?.total_items,
                data: result?.content,
                page: 1,
              }
            : {
                ...old,
                page,
                data: _.concat(old?.data, result?.content),
              };
        });
        setLoadExitsDataStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setLoadExitsDataStatus(QUERY_STATUS.ERROR);
        console.error("mutualDashboardExitsDataGetter", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const mutualDashboardOKDataGetter = useCallback(
    async ({ subsidiary, manager, start_at, date, page = 1 }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: MUTUAL_DASHBOARD_ROLES.ROLE_GET_MUTUAL_OK,
        })
      ) {
        return;
      }
      try {
        setLoadOKDataStatus(QUERY_STATUS.LOADING);
        const result = await getMutualDashboardOKQuery({
          scope: userScope,
          subsidiary,
          manager,
          start_at,
          date,
          page,
          setLoadOKDataStatus,
        });
        setMutualDashboardOKData((old) => {
          return _.isEmpty(old.data)
            ? {
                ...old,
                total: result?.total_items,
                data: result?.content,
                page: 1,
              }
            : {
                ...old,
                page,
                data: _.concat(old?.data, result?.content),
              };
        });
        setLoadOKDataStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setLoadOKDataStatus(QUERY_STATUS.ERROR);
        console.error("mutualDashboardOKDataGetter", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const values = useMemo(
    () => ({
      userRoleList,
      loadICDataStatus,
      loadFDMDataStatus,
      loadEDDataStatus,
      loadOKDataStatus,
      loadCLDataStatus,
      loadExitsDataStatus,
      mutualDashboardICData,
      mutualDashboardICDataGetter,
      mutualDashboardFDMData,
      mutualDashboardFDMDataGetter,
      mutualDashboardEDData,
      mutualDashboardEDDataGetter,
      mutualDashboardExitsDataGetter,
      mutualDashboardExitsData,
      setMutualDashboardExitsData,
      mutualDashboardCLData,
      mutualDashboardCLDataGetter,
      mutualDashboardOKData,
      mutualDashboardOKDataGetter,
      setMutualDashboardICData,
      setMutualDashboardFDMData,
      setMutualDashboardEDData,
      setMutualDashboardCLData,
      setMutualDashboardOKData,
      getMutualDashboardCSVFile,
      loadExportStatus,
      setLoadExportStatus,
    }),
    [
      userRoleList,
      loadICDataStatus,
      loadFDMDataStatus,
      loadEDDataStatus,
      loadOKDataStatus,
      loadCLDataStatus,
      loadExitsDataStatus,
      mutualDashboardICData,
      mutualDashboardICDataGetter,
      mutualDashboardFDMData,
      mutualDashboardFDMDataGetter,
      mutualDashboardEDData,
      mutualDashboardEDDataGetter,
      mutualDashboardExitsDataGetter,
      mutualDashboardExitsData,
      setMutualDashboardExitsData,
      mutualDashboardCLData,
      mutualDashboardCLDataGetter,
      mutualDashboardOKData,
      mutualDashboardOKDataGetter,
      setMutualDashboardICData,
      setMutualDashboardFDMData,
      setMutualDashboardEDData,
      setMutualDashboardCLData,
      setMutualDashboardOKData,
      getMutualDashboardCSVFile,
      loadExportStatus,
    ]
  );

  return <MutualDashboardContext.Provider value={values} {...props} />;
};
